import { useState, useEffect, useMemo } from "react";
import { alpha } from "@mui/material/styles";
import Box from "@mui/material/Box";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TablePagination from "@mui/material/TablePagination";
import TableRow from "@mui/material/TableRow";
import TableSortLabel from "@mui/material/TableSortLabel";
import Toolbar from "@mui/material/Toolbar";
import Typography from "@mui/material/Typography";
import Paper from "@mui/material/Paper";
import Checkbox from "@mui/material/Checkbox";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import AddIcon from "@mui/icons-material/Add";
import UpgradeIcon from '@mui/icons-material/Upgrade';
import CircularProgress from "@mui/material/CircularProgress";
import Alert from "@mui/material/Alert";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Button from "@mui/material/Button";
import { visuallyHidden } from "@mui/utils";
import api from "../../api";
import { useNavigate } from "react-router-dom";
import SnackBarComponent from "../../components/SnackBarComponent";
import { useDebounce } from "../../hooks/useDebounce";

// --- SEARCH IMPORTS ---
import TextField from "@mui/material/TextField";
import InputAdornment from "@mui/material/InputAdornment";
import SearchIcon from "@mui/icons-material/Search";
import usePermissions from "../../hooks/usePermissions";

function descendingComparator(a, b, orderBy) {
  if (b[orderBy] < a[orderBy]) return -1;
  if (b[orderBy] > a[orderBy]) return 1;
  return 0;
}

function getComparator(order, orderBy) {
  return order === "desc"
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}

const headCells = [
  { id: "name", numeric: false, disablePadding: true, label: "Name" },
  { id: "dealer_code", numeric: false, disablePadding: false, label: "Dealer Code" },
  { id: "location", numeric: false, disablePadding: false, label: "City, State" },
  { id: "operational", numeric: false, disablePadding: false, label: "isOperational" },
  { id: "action", numeric: false, disablePadding: false, label: "Actions" },
];

function EnhancedTableHead(props) {
  const { onSelectAllClick, order, orderBy, numSelected, rowCount, onRequestSort } = props;
  const createSortHandler = (property) => (event) => onRequestSort(event, property);

  return (
    <TableHead>
      <TableRow>
        <TableCell padding="checkbox">
          <Checkbox color="primary" indeterminate={numSelected > 0 && numSelected < rowCount} checked={rowCount > 0 && numSelected === rowCount} onChange={onSelectAllClick} />
        </TableCell>
        {headCells.map((headCell) => (
          <TableCell key={headCell.id} align={headCell.numeric ? "right" : "left"} padding={headCell.disablePadding ? "none" : "normal"} sortDirection={orderBy === headCell.id ? order : false}>
            <TableSortLabel active={orderBy === headCell.id} direction={orderBy === headCell.id ? order : "asc"} onClick={createSortHandler(headCell.id)}>
              {headCell.label}
              {orderBy === headCell.id ? <Box component="span" sx={visuallyHidden}>{order === "desc" ? "sorted descending" : "sorted ascending"}</Box> : null}
            </TableSortLabel>
          </TableCell>
        ))}
      </TableRow>
    </TableHead>
  );
}

function EnhancedTableToolbar(props) {

  const { permissions } = usePermissions();
  const isSuperAdmin = permissions.superAdmin;

  const { numSelected, handleAdd, handleEdit, handleDelete, searchQuery, setSearchQuery, handleUpdateToWebsite, buttonLoading } = props;
  return (
    <Toolbar sx={{ pl: { sm: 2 }, pr: { xs: 1, sm: 1 }, ...(numSelected > 0 && { bgcolor: (theme) => alpha(theme.palette.primary.main, theme.palette.action.activatedOpacity) }) }}>
      {numSelected > 0 ? (
        <Typography sx={{ flex: "1 1 100%" }} color="inherit" variant="subtitle1" component="div">{numSelected} selected</Typography>
      ) : (
        <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', gap: 2 }}>
          <Typography sx={{ flexShrink: 0 }} variant="h6" id="tableTitle" component="div">
            DEALERSHIP MASTER
          </Typography>

          <TextField
            variant="outlined"
            size="small"
            placeholder="Search by Name, Code or City"
            value={searchQuery || ""}
            onChange={(e) => setSearchQuery(e.target.value)}
            sx={{ ml: 'auto', width: 300 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" color="action" />
                </InputAdornment>
              ),
            }}
          />
        </Box>
      )}

      {numSelected === 1 ? (
        <Box sx={{ display: 'flex', ml: 2 }}>
          <Tooltip title="Delete"><IconButton onClick={handleDelete}><DeleteIcon /></IconButton></Tooltip>
        </Box>
      ) : (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {numSelected > 0 ? (
            <Tooltip title="Delete"><IconButton onClick={handleDelete}><DeleteIcon /></IconButton></Tooltip>
          ) : (
            <Box sx={{ display: 'flex', ml: 2, alignItems: 'center', gap: 1 }}>
              <Button variant="contained" color="primary" startIcon={<AddIcon />} onClick={handleAdd} sx={{ whiteSpace: 'nowrap' }}>Add Dealer</Button>
              {isSuperAdmin ? <Button variant="contained" color="error" startIcon={buttonLoading ? <CircularProgress size={20} color="inherit" /> : <UpgradeIcon />} disabled={buttonLoading} onClick={handleUpdateToWebsite} sx={{ whiteSpace: 'nowrap' }}>Update to Website</Button> : ""}
            </Box>

          )}
        </Box>
      )}

    </Toolbar>
  );
}

export default function MasterList() {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [buttonLoading, setButtonLoading] = useState(false);
  const [order, setOrder] = useState("asc");
  const [orderBy, setOrderBy] = useState("name");
  const [selected, setSelected] = useState([]);
  const [page, setPage] = useState(0);
  const [dense, setDense] = useState(false);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearchQuery] = useDebounce(searchQuery, 300);

  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  const [errorMessage, setErrorMessage] = useState(null);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);

  const fetchData = async () => {
    setIsLoading(true);
    const token = localStorage.getItem("accessToken");
    try {
      const response = await api.get("/api/dealer/all", { headers: { Authorization: `Bearer ${token}` } });
      setRows(response.data.data);
      setIsLoading(false);
    } catch (error) {
      setErrorMessage(error.response?.data?.message || "Error fetching data");
      setIsLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  const filteredRows = useMemo(() => {
    if (!debouncedSearchQuery) return rows;
    const lowerQuery = debouncedSearchQuery.toLowerCase();
    return rows.filter((row) =>
      (row.name?.toLowerCase() || "").includes(lowerQuery) ||
      (row.dealer_code?.toLowerCase() || "").includes(lowerQuery) ||
      (row.city?.toLowerCase() || "").includes(lowerQuery) ||
      (row.state?.toLowerCase() || "").includes(lowerQuery)
    );
  }, [rows, debouncedSearchQuery]);

  useEffect(() => {
    setPage(0);
  }, [debouncedSearchQuery]);

  const handleAdd = () => { navigate("/dealer/dealership-master/add"); };

  const handleUpdateToWebsite = async () => {
    try {
      const token = localStorage.getItem("accessToken");
      setButtonLoading(true);
      try {
        const response = await api.post("/api/dealer/refresh", { headers: { Authorization: `Bearer ${token}` } });
        if (response.status == 200) {
          setOpenSnackbar(true);
          setSnackbarMessage("Dealers refreshed successfully");
          setSnackbarSeverity("success");
        }
      } catch (error) {
        setOpenSnackbar(true);
        setSnackbarMessage(error.response?.data?.message || "Error fetching data");
        setSnackbarSeverity("error");
        setButtonLoading(false);
      }
      setButtonLoading(false);
    } catch (error) {
      setErrorMessage(error.response?.data?.message || "Error fetching data");
      setIsLoading(false);
    }
  };

  const handleEdit = () => {
    if (selected.length === 1) {
      const row = rows.find(r => r.dealer_code === selected[0].dealer_code);
      if (row) {
        navigate(`/dealer/dealership-master/edit/${row.dealer_code}/${row.country_code}`);
      }
    }
  };

  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const handleSelectAllClick = (event) => {
    if (event.target.checked) {
      const newSelecteds = filteredRows.map((n) => ({ dealer_code: n.dealer_code }));
      setSelected(newSelecteds);
      return;
    }
    setSelected([]);
  };

  const handleClick = (event, row) => {
    const exists = selected.find(item => item.dealer_code === row.dealer_code);
    let newSelected = [];
    if (exists) { newSelected = selected.filter(item => item.dealer_code !== row.dealer_code); }
    else { newSelected = [...selected, { dealer_code: row.dealer_code }]; }
    setSelected(newSelected);
  };

  const handleChangePage = (event, newPage) => setPage(newPage);
  const handleChangeRowsPerPage = (event) => { setRowsPerPage(parseInt(event.target.value, 10)); setPage(0); };
  const isSelected = (dealer_code) => selected.some(item => item.dealer_code === dealer_code);
  const emptyRows = page > 0 ? Math.max(0, (1 + page) * rowsPerPage - filteredRows.length) : 0;

  const handleDeleteConfirm = async () => {
    const token = localStorage.getItem("accessToken");
    // Ensure this matches your backend route
    const deleteUrl = "/api/dealer/delete";

    try {
      const deletePromises = selected.map((item) => {
        // robust find
        const row = rows.find(r => String(r.dealer_code) === String(item.dealer_code));

        if (!row) {
          console.error(`Row not found for dealer_code: ${item.dealer_code}`);
          return Promise.resolve();
        }

        // FIX: Use POST instead of DELETE
        // Syntax: axios.post(url, data, config)
        return api.post(deleteUrl, {
          dealer_code: item.dealer_code,
          country_code: row.country_code
        }, {
          headers: { Authorization: `Bearer ${token}` }
        });
      });

      // Wait for all posts to finish
      await Promise.all(deletePromises);

      // Optimistic UI Update
      const deletedCodes = selected.map(s => String(s.dealer_code));
      setRows(prevRows => prevRows.filter(r => !deletedCodes.includes(String(r.dealer_code))));

      setSelected([]);
      setOpenDeleteDialog(false);
      setSnackbarMessage("Deleted successfully!");
      setSnackbarSeverity("success");
      setOpenSnackbar(true);

    } catch (error) {
      console.error("Delete Failed", error);
      setSnackbarMessage(error.response?.data?.message || "Failed to delete");
      setSnackbarSeverity("error");
      setOpenSnackbar(true);
    }
  };
  const handleCloseSnackbar = (event, reason) => { if (reason === "clickaway") return; setOpenSnackbar(false); };
  const handleDeleteClick = () => {
    setOpenDeleteDialog(true);
  };
  const handleDeleteCancel = () => setOpenDeleteDialog(false);

  const visibleRows = useMemo(() => [...filteredRows].sort(getComparator(order, orderBy)).slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage), [order, orderBy, page, rowsPerPage, filteredRows]);

  return (
    <Box sx={{ width: "100%" }}>
      {openSnackbar && <SnackBarComponent message={snackbarMessage} severity={snackbarSeverity} onClose={handleCloseSnackbar} />}
      <Paper sx={{ width: "100%", mb: 2 }}>
        <EnhancedTableToolbar
          numSelected={selected.length}
          handleAdd={handleAdd}
          handleUpdateToWebsite={handleUpdateToWebsite}
          handleEdit={handleEdit}
          handleDelete={handleDeleteClick}
          searchQuery={searchQuery}
          setSearchQuery={setSearchQuery}
        />
        <TableContainer>
          <Table sx={{ minWidth: 750 }} aria-labelledby="tableTitle" size={dense ? "small" : "medium"}>
            <EnhancedTableHead numSelected={selected.length} order={order} orderBy={orderBy} onSelectAllClick={handleSelectAllClick} onRequestSort={handleRequestSort} rowCount={filteredRows.length} />
            <TableBody>
              {isLoading ? (
                <TableRow><TableCell colSpan={6} align="center"><CircularProgress size="2rem" /></TableCell></TableRow>
              ) : visibleRows.length === 0 ? (
                <TableRow><TableCell colSpan={6} align="center"><Alert severity="info">{errorMessage || "No data available."}</Alert></TableCell></TableRow>
              ) : (
                visibleRows.map((row, index) => {
                  const isItemSelected = isSelected(row.dealer_code);
                  const labelId = `enhanced-table-checkbox-${index}`;
                  return (
                    <TableRow hover onClick={(event) => handleClick(event, row)} role="checkbox" aria-checked={isItemSelected} tabIndex={-1} key={`${row.dealer_code}-${row.country_code}`} selected={isItemSelected} sx={{ cursor: "pointer" }}>
                      <TableCell padding="checkbox"><Checkbox color="primary" checked={isItemSelected} inputProps={{ "aria-labelledby": labelId }} /></TableCell>

                      <TableCell>{row.name}</TableCell>
                      <TableCell>{row.dealer_code}</TableCell>
                      <TableCell>{row.city}, {row.state}</TableCell>
                      <TableCell sx={{ color: row.operational ? "success.main" : "error.main", fontWeight: 'bold' }}>
                        {row.operational ? "Yes" : "No"}
                      </TableCell>

                      <TableCell>
                        <Tooltip title="Edit">
                          <IconButton onClick={() => navigate(`/dealer/dealership-master/edit/${row.dealer_code}/${row.country_code}`)}>
                            <EditIcon />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
              {emptyRows > 0 && <TableRow style={{ height: (dense ? 33 : 53) * emptyRows }}><TableCell colSpan={6} /></TableRow>}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination rowsPerPageOptions={[25, 50, 100]} component="div" count={filteredRows.length} rowsPerPage={rowsPerPage} page={page} onPageChange={handleChangePage} onRowsPerPageChange={handleChangeRowsPerPage} />
      </Paper>
      <Dialog open={openDeleteDialog} onClose={handleDeleteCancel}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>Are you sure you want to delete the selected {selected.length} item(s)? This action cannot be undone.</DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel}>Cancel</Button>
          <Button onClick={handleDeleteConfirm} color="error">Delete</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}